import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { BEResponse } from '@qtek/core/api-core';
import {
  BaseWebsocketMessage,
  DelWebsocketMessageRequest,
  GetWebsocketMessageRequest,
  InsWebsocketMessageRequest,
  QueryWebsocketMessageRequest,
  SubWebsocketMessageRequest,
  UpdWebsocketMessageRequest,
  WebsocketEntTypes,
  WebSocketService,
  WSEntityManager,
} from '@qtek/core/websockets-core';
import { BackendResponse, Item, UserApproval } from '@qtek/shared/models';
import { Observable } from 'rxjs';

const NDA_APPROVAL_BY_USER_APPROVAL: Record<UserApproval, string> = {
  accepted: 'APPROVAL',
  declined: 'DECLINED',
};

@Injectable({
  providedIn: 'root',
})
export class ItemService {
  private websocketService = inject(WebSocketService);
  protected http = inject(HttpClient);

  entityName: WebsocketEntTypes = 'Item';
  wsManager = new WSEntityManager(this.websocketService, this.entityName);

  subscribe(
    payload: Omit<SubWebsocketMessageRequest, 'ent' | 'op'>,
    messageFilter?: (message: BaseWebsocketMessage) => boolean
  ): Observable<BaseWebsocketMessage> {
    return this.wsManager.subscribeEntity(payload, true, messageFilter);
  }

  createItem(payload: Omit<InsWebsocketMessageRequest, 'ent' | 'op'>) {
    this.wsManager.create(payload);
  }

  updateItem(payload: Omit<UpdWebsocketMessageRequest, 'ent' | 'op'>) {
    this.wsManager.update(payload);
  }

  deleteItem(payload: Omit<DelWebsocketMessageRequest, 'ent' | 'op'>) {
    this.wsManager.delete(payload);
  }

  getItems(payload: Omit<QueryWebsocketMessageRequest, 'ent' | 'op'>) {
    this.wsManager.query(payload);
  }

  getItemById(payload: Omit<GetWebsocketMessageRequest, 'ent' | 'op'>) {
    this.wsManager.get(payload);
  }

  mintItem(seqno: number): Observable<BackendResponse<any>> {
    return this.http.post<BackendResponse>(
      '/pt/api/v1/mint',
      { seqno },
      { withCredentials: true }
    );
  }

  bulkCreateItems(items: Item[]): Observable<BEResponse<Item[]>> {
    // v2 ItemEntityTask.bulkUpsert recursively upserts Item.subItems, parenting
    // each to the row created just before it (SD-2270).
    return this.http.post<BEResponse<Item[]>>(
      '/api/v2/model/Item/bulk',
      items,
      {
        withCredentials: true,
      }
    );
  }

  sendNdaEvent(
    parId: string,
    userApproval: UserApproval
  ): Observable<BackendResponse<any>> {
    return this.http.post<BackendResponse>(
      `/api/v2/nda/event/${parId}`,
      { userApproval: NDA_APPROVAL_BY_USER_APPROVAL[userApproval] },
      { withCredentials: true }
    );
  }
}
